/**
 * 着色器方言（M1-11 / DESIGN.md §2.8、§4.4）。
 *
 * 双后端要求每个着色器都有 WGSL 与 GLSL 两份。手写两份是**必然会出错**的做法：
 * 十二种混合模式改一个公式就要改两处，漏改一处的表现是"某个模式在某个后端上
 * 差几个色阶"——这种 bug 不会崩，只会静静地把用户的图算错。
 *
 * 所以只写一份公式，两种语法从它生成。两种语言在算术上几乎一样，差异集中在四处：
 * 类型名、向量常量的构造、逐分量选择、函数声明语法。把这四处抽成方言，
 * 公式本身就与语言无关了。
 *
 * 这一层**不做通用的着色器编译**——那是另一个量级的工程。它只覆盖可分离混合
 * 需要的那点语法，能力刚好够用，边界清楚。
 */

/** 着色器方言。每种方言把与语言无关的公式渲染成该语言的源码。 */
export interface ShaderDialect {
  readonly name: 'wgsl' | 'glsl';
  /** 三分量浮点向量的类型名。 */
  readonly vec3: string;
  /** 把标量常量铺成向量，如 `vec3f(0.5)`。 */
  splat(value: number): string;
  /** 局部 vec3 常量声明（含结尾分号）。 */
  local(name: string, expr: string): string;
  /** 任意类型的局部常量声明。非可分离模式要算标量亮度，vec3 不够用。 */
  localOf(type: ShaderType, name: string, expr: string): string;
  /** 标量字面量。WGSL 的 f32 与 GLSL 的 float 写法相同，但整数要带小数点。 */
  scalar(value: number): string;
  /**
   * 逐分量选择。
   *
   * 参数顺序刻意跟着 WGSL 的 `select(falseValue, trueValue, cond)` 走，
   * 而不是三元运算符的直觉顺序——写公式时只需记住一种顺序，
   * 记混的代价是两个后端结果不一致，而那恰恰是最难发现的一类 bug。
   */
  select(whenFalse: string, whenTrue: string, cond: string): string;
  /** 逐分量 `a <= b`，结果是布尔向量。 */
  lessEqual(a: string, b: string): string;
  /** 逐分量 `a >= b`。 */
  greaterEqual(a: string, b: string): string;
  /** 定义一个函数。 */
  functionDef(
    name: string,
    params: readonly ShaderParam[],
    returns: ShaderType,
    body: string,
  ): string;
  /** 声明一个 uniform 块。两种后端的 uniform 语法差得最远，整块交给方言。 */
  uniformBlock(fields: readonly ShaderParam[]): string;
  /** 引用 uniform 字段。 */
  uniform(name: string): string;
  /** 全屏三角形的顶点阶段与片元阶段包装。 */
  fullscreenPreamble(): string;
  fragmentMain(body: string): string;
}

export type ShaderType = 'vec3' | 'vec4' | 'float';

export interface ShaderParam {
  readonly name: string;
  readonly type: ShaderType;
}

const WGSL_TYPES: Readonly<Record<ShaderType, string>> = {
  vec3: 'vec3f',
  vec4: 'vec4f',
  float: 'f32',
};
const GLSL_TYPES: Readonly<Record<ShaderType, string>> = {
  vec3: 'vec3',
  vec4: 'vec4',
  float: 'float',
};

export const WGSL_DIALECT: ShaderDialect = {
  name: 'wgsl',
  vec3: 'vec3f',
  splat: (value) => `vec3f(${formatFloat(value)})`,
  local: (name, expr) => `let ${name} = ${expr};`,
  localOf: (_type, name, expr) => `let ${name} = ${expr};`,
  scalar: (value) => formatFloat(value),
  select: (whenFalse, whenTrue, cond) => `select(${whenFalse}, ${whenTrue}, ${cond})`,
  // WGSL 的比较运算符对向量逐分量生效，直接得到 vec3<bool>
  lessEqual: (a, b) => `(${a} <= ${b})`,
  greaterEqual: (a, b) => `(${a} >= ${b})`,
  functionDef: (name, params, returns, body) => {
    const args = params.map((p) => `${p.name}: ${WGSL_TYPES[p.type]}`).join(', ');
    return `fn ${name}(${args}) -> ${WGSL_TYPES[returns]} {\n${indent(body)}\n}`;
  },
  uniformBlock: (fields) => {
    // 全部 uniform 必须挤进**同一个** binding。M0-11 上踩过：分成多个 binding 后
    // 取 bind group layout 的那次探测会让整个 command encoder 失效，
    // 渲染静默变全黑、不抛任何异常。
    const members = uniformLayout(fields)
      .map((f) => `  ${f.name}: ${WGSL_TYPES[f.type]},`)
      .join('\n');
    return `struct Params {\n${members}\n};\n@group(0) @binding(0) var<uniform> params: Params;`;
  },
  uniform: (name) => `params.${name}`,
  fullscreenPreamble: () => `@vertex
fn vs_main(@builtin(vertex_index) idx: u32) -> @builtin(position) vec4f {
  var pos = array<vec2f, 3>(vec2f(-1.0, -1.0), vec2f(3.0, -1.0), vec2f(-1.0, 3.0));
  return vec4f(pos[idx], 0.0, 1.0);
}`,
  fragmentMain: (body) => `@fragment\nfn fs_main() -> @location(0) vec4f {\n${indent(body)}\n}`,
};

export const GLSL_DIALECT: ShaderDialect = {
  name: 'glsl',
  vec3: 'vec3',
  splat: (value) => `vec3(${formatFloat(value)})`,
  // GLSL 没有 `let`；用 const 会禁止依赖参数的初始化，所以就用普通局部变量
  local: (name, expr) => `vec3 ${name} = ${expr};`,
  localOf: (type, name, expr) => `${GLSL_TYPES[type]} ${name} = ${expr};`,
  scalar: (value) => formatFloat(value),
  // GLSL ES 3.0 的 mix 接受 bvec 作为选择子，语义与 WGSL 的 select 一致
  select: (whenFalse, whenTrue, cond) => `mix(${whenFalse}, ${whenTrue}, ${cond})`,
  // GLSL 的比较运算符不接受向量，必须用逐分量函数
  lessEqual: (a, b) => `lessThanEqual(${a}, ${b})`,
  greaterEqual: (a, b) => `greaterThanEqual(${a}, ${b})`,
  functionDef: (name, params, returns, body) => {
    const args = params.map((p) => `${GLSL_TYPES[p.type]} ${p.name}`).join(', ');
    return `${GLSL_TYPES[returns]} ${name}(${args}) {\n${indent(body)}\n}`;
  },
  // WebGL2 后端把 uniform 逐个 setUniform，没有块的概念
  uniformBlock: (fields) =>
    uniformLayout(fields)
      .map((f) => `uniform ${GLSL_TYPES[f.type]} ${f.name};`)
      .join('\n'),
  uniform: (name) => name,
  fullscreenPreamble: () => `#version 300 es
void main() {
  vec2 pos[3] = vec2[3](vec2(-1.0, -1.0), vec2(3.0, -1.0), vec2(-1.0, 3.0));
  gl_Position = vec4(pos[gl_VertexID], 0.0, 1.0);
}`,
  fragmentMain: (body) => `void main() {\n${indent(body)}\n}`,
};

export const DIALECTS: readonly ShaderDialect[] = [WGSL_DIALECT, GLSL_DIALECT];

/**
 * 浮点字面量。
 *
 * 必须带小数点：WGSL 里 `16` 是 i32，`16 * cb` 会因类型不匹配而编译失败。
 * GLSL 同理。这是从同一份公式生成两种语言时最容易踩的一脚。
 */
export function formatFloat(value: number): string {
  return Number.isInteger(value) ? `${value}.0` : `${value}`;
}

function indent(text: string): string {
  return text
    .split('\n')
    .map((line) => (line.trim() === '' ? line : `  ${line}`))
    .join('\n');
}

/**
 * uniform 字段的最终布局。
 *
 * 两条约束都来自后端已有的打包方式（见 webgpu/backend.ts 的 `packUniforms`），
 * 违反了不会报错，只会让着色器读到**错位的数值**——画面还是出得来，只是全错，
 * 是最难查的一类问题。所以这里直接把它们变成硬规则：
 *
 * 1. **按名字字母序排列。** 后端按 `Object.keys(uniforms).sort()` 打包，
 *    结构体的字段顺序必须与之一致。
 * 2. **不许用 vec3。** 后端把数值紧密排布、不插对齐填充，而 WGSL 的 uniform
 *    地址空间要求 vec3f 按 16 字节对齐——两者对不上。vec4 的自然对齐正好是 16，
 *    与紧密打包一致，所以三分量的量一律声明成 vec4、在着色器里取 `.rgb`。
 */
export function uniformLayout(fields: readonly ShaderParam[]): readonly ShaderParam[] {
  for (const field of fields) {
    if (field.type === 'vec3') {
      throw new RangeError(
        `uniform 字段「${field.name}」不能是 vec3：` +
          `WGSL 要求它按 16 字节对齐，而后端是紧密打包的。请改用 vec4 并在着色器里取 .rgb。`,
      );
    }
  }
  return [...fields].sort((a, b) => (a.name < b.name ? -1 : a.name > b.name ? 1 : 0));
}
